import Alpine from 'alpinejs';

Alpine.data('anchors', () => ({
  activeIndex: null as string | null,
  observer: null as IntersectionObserver | null,
  isAutoScrolling: false,
  settleTimer: null as number | null,
  canScrollLeft: false,
  canScrollRight: false,

  init() {
    const SCROLL_SETTLE_MS = 140;
    const tabButtons = document.querySelectorAll('[data-index]');
    const anchorIds = Array.from(tabButtons).map((btn) => (btn as HTMLElement).dataset.index!).filter(Boolean);
    const sections = anchorIds.map((id) => document.getElementById(id)).filter(Boolean) as HTMLElement[];

    this.observer = new IntersectionObserver(
      (entries) => {
        if (this.isAutoScrolling) return;
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.activeIndex = (entry.target as HTMLElement).id;
          }
        }
      },
      {
        root: null,
        rootMargin: '-100px 0px -50% 0px',
        threshold: 0.1,
      },
    );

    sections.forEach((sec) => this.observer!.observe(sec));
    if (anchorIds.length) this.activeIndex = anchorIds[0];

    this.$nextTick(() => this.updateScrollButtons());
    window.addEventListener('resize', () => this.updateScrollButtons());

    window.addEventListener(
      'scroll',
      () => {
        if (this.isAutoScrolling) {
          if (this.settleTimer) clearTimeout(this.settleTimer);
          this.settleTimer = setTimeout(() => {
            this.isAutoScrolling = false;
          }, SCROLL_SETTLE_MS) as any;
        }
      },
      { passive: true },
    );
  },

  scrollToIndex(idx: string) {
    const target = document.getElementById(idx);
    if (!target) return;
    this.isAutoScrolling = true;
    this.activeIndex = idx;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (this.settleTimer) clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => {
      this.isAutoScrolling = false;
    }, 800) as any;
  },

  scrollActiveTabIntoView() {
    const bar = this.$refs.tabbar;
    if (!bar || this.activeIndex == null) return;
    const btn = bar.querySelector(`[data-index="${this.activeIndex}"]`);
    if (!btn) return;
    const targetLeft =
      (btn as HTMLElement).offsetLeft - bar.clientWidth / 2 + (btn as HTMLElement).clientWidth / 2;
    try {
      bar.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' });
    } catch {
      bar.scrollLeft = Math.max(0, targetLeft);
    }
    this.updateScrollButtons();
  },

  scrollTabsRight() {
    const bar = this.$refs.tabbar;
    if (!bar) return;

    const btns = bar.querySelectorAll('[data-index]');
    if (!btns.length) return;

    const padLeft = parseFloat(getComputedStyle(bar).paddingLeft || '0');
    const currentLeft = bar.scrollLeft;
    const tol = 2;

    let target = null;
    for (let i = 0; i < btns.length; i++) {
      const b = btns[i];
      if ((b as HTMLElement).offsetLeft > currentLeft + padLeft + tol) {
        target = b;
        break;
      }
    }

    const maxScroll = bar.scrollWidth - bar.clientWidth;
    if (!target) {
      try {
        bar.scrollTo({ left: maxScroll, behavior: 'smooth' });
      } catch {
        bar.scrollLeft = maxScroll;
      }
      this.updateScrollButtons();
      return;
    }

    const newLeft = Math.max(0, Math.min(maxScroll, (target as HTMLElement).offsetLeft - padLeft));
    try {
      bar.scrollTo({ left: newLeft, behavior: 'smooth' });
    } catch {
      bar.scrollLeft = newLeft;
    }

    this.updateScrollButtons();
  },

  scrollTabsLeft() {
    const bar = this.$refs.tabbar;
    if (!bar) return;

    const btns = bar.querySelectorAll('[data-index]');
    if (!btns.length) return;

    const padLeft = parseFloat(getComputedStyle(bar).paddingLeft || '0');
    const currentLeft = bar.scrollLeft;
    const tol = 2;

    let target = null;
    for (let i = btns.length - 1; i >= 0; i--) {
      const b = btns[i];
      if ((b as HTMLElement).offsetLeft < currentLeft + padLeft - tol) {
        target = b;
        break;
      }
    }

    if (!target) {
      try {
        bar.scrollTo({ left: 0, behavior: 'smooth' });
      } catch {
        bar.scrollLeft = 0;
      }
      this.updateScrollButtons();
      return;
    }

    const newLeft = Math.max(0, (target as HTMLElement).offsetLeft - padLeft);
    try {
      bar.scrollTo({ left: newLeft, behavior: 'smooth' });
    } catch {
      bar.scrollLeft = newLeft;
    }

    this.updateScrollButtons();
  },

  updateScrollButtons() {
    const bar = this.$refs.tabbar;
    if (!bar) return;
    const maxScroll = bar.scrollWidth - bar.clientWidth;
    const x = bar.scrollLeft;
    const tol = 2;
    this.canScrollLeft = x > tol;
    this.canScrollRight = x < maxScroll - tol;
  },
}));
